import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Droplets, ArrowLeft, Zap, Clock, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Navbar";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | register
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const routeFor = (user) => (user.role === "admin" ? "/admin" : user.role === "driver" ? "/driver" : "/account");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = mode === "signin"
        ? await login(form.email, form.password)
        : await register({ email: form.email, password: form.password, name: form.name, phone: form.phone });
      toast.success(mode === "signin" ? `Welcome back, ${user.name}` : `Welcome, ${user.name}!`);
      navigate(routeFor(user));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 navy-bg relative overflow-hidden items-center justify-center p-16">
        <div className="absolute -left-24 -bottom-24 w-96 h-96 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="relative max-w-md">
          <div className="brand-gradient w-16 h-16 rounded-2xl flex items-center justify-center water-drop-shadow mb-8">
            <Droplets className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-4xl font-extrabold text-white leading-tight">Book water in seconds, every time.</h1>
          <p className="text-slate-300 mt-4">Create a free account to save your details, addresses and payment — then rebook with a single tap.</p>
          <div className="mt-8 space-y-3">
            {[[Zap, "Saved details for instant booking"], [Clock, "One-tap pay with a saved card"], [MapPin, "Multiple saved delivery addresses"]].map(([Icon, t]) => (
              <div key={t} className="flex items-center gap-3 text-slate-200 text-sm">
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"><Icon className="w-4 h-4 text-cyan-300" /></div>{t}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-sm">
          <div className="mb-8"><Logo /></div>
          <Link to="/" className="text-sm text-slate-500 hover:text-blue-600 flex items-center gap-1 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>

          <div className="flex gap-1 bg-slate-100 rounded-full p-1 mb-6 w-fit">
            <button onClick={() => setMode("signin")} data-testid="auth-tab-signin"
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${mode === "signin" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>Sign in</button>
            <button onClick={() => setMode("register")} data-testid="auth-tab-register"
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${mode === "register" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>Create account</button>
          </div>

          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-1">{mode === "signin" ? "Sign in" : "Create your account"}</h2>
          <p className="text-slate-500 text-sm mb-6">{mode === "signin" ? "Welcome back to Blake's Quality Water." : "Save your details and book faster next time."}</p>

          <button onClick={google} data-testid="google-auth-btn"
            className="w-full border border-slate-200 bg-white hover:bg-slate-50 rounded-xl py-3 flex items-center justify-center gap-2.5 text-sm font-semibold text-slate-700 transition-colors mb-4">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Continue with Google
          </button>
          <div className="flex items-center gap-3 mb-4"><div className="h-px bg-slate-200 flex-1" /><span className="text-xs text-slate-400">or</span><div className="h-px bg-slate-200 flex-1" /></div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <Field label="Full name" value={form.name} onChange={set("name")} testid="register-name-input" placeholder="Sarah Mitchell" required />
                <Field label="Phone" value={form.phone} onChange={set("phone")} testid="register-phone-input" placeholder="021 234 5678" />
              </>
            )}
            <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="login-email-input" placeholder="you@example.com" required />
            <Field label="Password" type="password" value={form.password} onChange={set("password")} testid="login-password-input" placeholder="••••••••" required />
            <button type="submit" disabled={loading}
              className="brand-gradient btn-shine text-white font-semibold w-full py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="login-submit-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : mode === "signin" ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="text-xs text-slate-400 text-center mt-5">
            {mode === "signin" ? "New here? " : "Already have an account? "}
            <button onClick={() => setMode(mode === "signin" ? "register" : "signin")} className="text-blue-600 font-semibold hover:underline">
              {mode === "signin" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, testid, type = "text", ...props }) => (
  <div>
    <label className="text-xs font-medium text-slate-600 mb-1.5 block">{label}</label>
    <input type={type} data-testid={testid} {...props}
      className="w-full border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white text-sm" />
  </div>
);
